import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './DemoAvailability.css';

const DemoAvailability = ({ userData }) => {
  const [scheduleData, setScheduleData] = useState({ schedule: [], availability: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [checkedSlots, setCheckedSlots] = useState({});
  const [notification, setNotification] = useState(null);

  // Generate today + next 4 days
  const generateDays = () => {
    const days = [];
    const today = new Date();

    // Add today + next 4 days
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push(date);

    }

    return days;
  };
  const calculateTopOffset = (sessionTime, slotTime) => {
    // Parse the session time (actual class time)
    const sessionDate = new Date(sessionTime);
    const sessionHours = sessionDate.getHours();
    const sessionMinutes = sessionDate.getMinutes();
    const sessionTotalMinutes = sessionHours * 60 + sessionMinutes;
  
    // Parse the slot time (displayed time slot)
    const [slotTimePart, slotPeriod] = slotTime.trim().split(' ');
    let [slotHours, slotMinutes] = slotTimePart.split(':').map(Number);
    
    // Convert to 24-hour format
    if (slotPeriod === 'PM' && slotHours !== 12) slotHours += 12;
    if (slotPeriod === 'AM' && slotHours === 12) slotHours = 0;
    
    const slotTotalMinutes = slotHours * 60 + (slotMinutes || 0);
  
    // Calculate difference in minutes
    const diffMinutes = sessionTotalMinutes - slotTotalMinutes;
  
    // Convert minutes to pixels (assuming 60px per hour)
    return (diffMinutes / 60) * 60; // 60px per hour, so 1px per minute
  };
  const days = generateDays();

  // Time slots with half-hour increments in the afternoon
  const timeSlots = [
    '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM',
    '2:30 PM', '3:30 PM', '4:30 PM', '5:30 PM', '6:30 PM', 
    '7:30 PM', '8:30 PM', '9:30 PM', '10:30 PM'
  ];

  // Check if a time slot is unavailable (current time + 2 hours for today only)
  const isSlotUnavailable = (day, time) => {
    const today = new Date();
    const slotDate = new Date(day);
    const isToday = slotDate.toDateString() === today.toDateString();
  
    if (!isToday) return false; // Only today's slots can be unavailable
  
    const now = new Date();
  
    // Extract time and period (AM/PM)
    const [timePart, period] = time.trim().split(' ');
    const [hoursStr, minutesStr] = timePart.split(':');
    let hours = Number(hoursStr);
    const minutes = Number(minutesStr);
  
    // Convert to 24-hour format
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
  
    slotDate.setHours(hours, minutes, 0, 0);
  
    // Add 2 hours buffer to current time
    const cutoffTime = new Date(now.getTime() + 2 * 60 * 60 * 1000);
  
    return slotDate < cutoffTime;
  };

  // Fetch demo availability data from API
  useEffect(() => {
    const fetchAvailabilityData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/teacher/demoavailability/${userData.id}`
        );
        setScheduleData(response.data);
        
        // Initialize checked slots from API
        const initialChecked = {};
        response.data.availability.forEach(slot => {
          if (slot.Check === 1) {
            const date = new Date(slot.AdjustedDateTime);
            const dayKey = date.getFullYear() + '-' + 
                 String(date.getMonth() + 1).padStart(2, '0') + '-' + 
                 String(date.getDate()).padStart(2, '0');
            const time = formatTime(slot.AdjustedDateTime);
            const slotKey = `${dayKey}-${time}`;
            initialChecked[slotKey] = true;
            
          }
        });
        setCheckedSlots(initialChecked);
      } catch (error) {
        console.error('Error fetching availability:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvailabilityData();
  }, [userData.id]);

  // Format time from ISO string to AM/PM
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    
    if (hours === 0) {
      return `12:${minutes.toString().padStart(2, '0')} AM`;
    } else if (hours < 12) {
      return `${hours}:${minutes.toString().padStart(2, '0')} AM`;
    } else if (hours === 12) {
      return `12:${minutes.toString().padStart(2, '0')} PM`;
    } else {
      return `${hours - 12}:${minutes.toString().padStart(2, '0')} PM`;
    }
  };
  const alignSessionToSlot = (sessionTime, slots) => {
    const sessionDate = new Date(sessionTime);
    const sessionHours = sessionDate.getHours();
    const sessionMinutes = sessionDate.getMinutes();
    
    // Find the closest slot
    for (let i = 0; i < slots.length; i++) {
      const [timePart, period] = slots[i].trim().split(' ');
      let [hoursStr, minutesStr] = timePart.split(':');
      let hours = Number(hoursStr);
      const minutes = Number(minutesStr || '0');
      
      // Convert to 24-hour format
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      
      // Check if session matches this slot or should be aligned to previous slot
      if (sessionHours === hours && sessionMinutes === minutes) {
        return slots[i]; // Exact match
      }
      
      // Special alignment rules
      if (sessionHours === hours ) {
        // If session is at :30, show in previous slot (e.g., 6:00 session shows at 5:30 slot)
        return slots[i];
      }
      
      
    }
    
    return null; // No matching slot
  };
  const submitAvailability = async (day, time, isChecked) => {
    try {
      const date = new Date(day);
  
      // Properly parse time and period
      const [timePart, period] = time.trim().split(' ');
      const [hourStr, minuteStr] = timePart.split(':');
      let hours = Number(hourStr);
      const minutes = Number(minuteStr);
  
      // Convert to 24-hour format
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
  
      date.setHours(hours, minutes, 0, 0);
  
      // Format to local ISO-like string (no time zone offset)
      const pad = (n) => (n < 10 ? '0' + n : n);
      const localDatetime = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/teacher/submitdemoavailability`, {
        teacherId: userData.id,
        datetime: localDatetime,
        isAvailable: isChecked
      });
  
      showNotification(
        `Slot ${time} ${isChecked ? 'marked as available' : 'unmarked'}`,
        isChecked ? 'green' : 'red'
      );
    } catch (error) {
      console.error('Error submitting availability:', error);
      showNotification('Failed to update availability', 'red');
    }
  };

  const handleCheckboxChange = async (day, time) => {
    const dayKey = day.getFullYear() + '-' + 
                 String(day.getMonth() + 1).padStart(2, '0') + '-' + 
                 String(day.getDate()).padStart(2, '0');
    const slotKey = `${dayKey}-${time}`;
    const isCurrentlyChecked = checkedSlots[slotKey];
    const newChecked = !isCurrentlyChecked;
    
    // Optimistic UI update
    setCheckedSlots(prev => ({
      ...prev,
      [slotKey]: newChecked
    }));
    
    // Submit to API
    await submitAvailability(day, time, newChecked);
  };

  const showNotification = (message, color) => {
    setNotification({ message, color });
    setTimeout(() => setNotification(null), 1500);
  };

  // Organize data by day and time
  const organizeData = () => {
    const organized = {};

    // Initialize empty structure
    days.forEach(day => {


  // Format as YYYY-MM-DD in local time
  const dayKey = day.getFullYear() + '-' + 
                 String(day.getMonth() + 1).padStart(2, '0') + '-' + 
                 String(day.getDate()).padStart(2, '0');


      organized[dayKey] = {};
      timeSlots.forEach(time => {
        organized[dayKey][time] = []; // Now stores an array
      });

    });

    // Add schedule items
    scheduleData.schedule.forEach(session => {
      const date = new Date(session.ClassDateTime);
      const dayKey = date.getFullYear() + '-' + 
                 String(date.getMonth() + 1).padStart(2, '0') + '-' + 
                 String(date.getDate()).padStart(2, '0');
      const alignedTime = alignSessionToSlot(session.ClassDateTime, timeSlots);

      if (alignedTime && organized[dayKey] && organized[dayKey][alignedTime]) {
        organized[dayKey][alignedTime].push({
          ...session,
          type: session.Type === 0 ? 'paid' : session.Type === 1 ? 'demo' : 'group',
          actualTime: formatTime(session.ClassDateTime),

          originalTime: session.ClassDateTime
        });

      }
    });
    
    // Add availability items (these should still be single items)
    scheduleData.availability.forEach(slot => {
      const date = new Date(slot.AdjustedDateTime);
      const dayKey = date.getFullYear() + '-' + 
                 String(date.getMonth() + 1).padStart(2, '0') + '-' + 
                 String(date.getDate()).padStart(2, '0');
      const time = formatTime(slot.AdjustedDateTime);

      if (organized[dayKey] && organized[dayKey][time]) {
        // Only add availability if there are no sessions
        if (organized[dayKey][time].length === 0) {
          organized[dayKey][time].push({
            isAvailability: true,
            isChecked: slot.Check === 1,
            isTaken: slot.Taken === 1,
            actualTime: time
          });
        }
      }
    });

    return organized;
  };;

  const organizedData = organizeData();

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Demo Availability...</p>
      </div>
    );
  }

  return (
    <div className="demo-schedule">
      {notification && (
        <div className="notification" style={{ backgroundColor: notification.color }}>
          {notification.message}
        </div>
      )}

      <div className="table-header">
        <h3>Demo Availability - Next 4 Days</h3>
      </div>

      <div className="schedule-table">
        <div className="table-body">
          <div className="time-column">
            <div className="time-header">Time</div>
            {timeSlots.map(time => (
              <div key={time} className={`time-slot-label ${time==="1:00 PM"?'bigger':''}`} >{time}</div>
            ))}
          </div>

          <div className="days-container">
            <div className="days-header">
              {days.map((day, index) => (
                <div key={day} className={`day-header ${index >= 5 ? 'upcoming-day' : ''}`}>
                  {formatDayHeader(day)}
                  {index === 0 && <span className="today-badge">Today</span>}
                  
                </div>
              ))}
            </div>

            <div className="schedule-grid">
              {timeSlots.map(time => (
                <div key={time} className="time-row">
                  {days.map((day, dayIndex) => {
                     const dayKey = day.getFullYear() + '-' + 
                 String(day.getMonth() + 1).padStart(2, '0') + '-' + 
                 String(day.getDate()).padStart(2, '0');
                     const slotData = organizedData[dayKey]?.[time] || null;

                     const isUnavailable = dayIndex === 0 && isSlotUnavailable(day, time);
                     const isUpcomingDay = dayIndex >= 5;
                     const slotKey = `${dayKey}-${time}`;
                     const isChecked = checkedSlots[slotKey];
                     const isToday = dayIndex === 0;
                     
                     // Calculate time difference in minutes
                     let diffMinutes = 0;
                     if (slotData && slotData.originalTime) {
                       // Parse slot time
                       const [slotTimePart, slotPeriod] = time.split(' ');
                       let [slotHours, slotMinutes] = slotTimePart.split(':').map(Number);
                       if (slotPeriod === 'PM' && slotHours !== 12) slotHours += 12;
                       if (slotPeriod === 'AM' && slotHours === 12) slotHours = 0;
                       
                       // Parse session time
                       const sessionDate = new Date(slotData.originalTime);
                       const sessionHours = sessionDate.getHours();
                       const sessionMinutes = sessionDate.getMinutes();
                       
                       // Calculate difference in minutes
                       diffMinutes = (sessionHours * 60 + sessionMinutes) - (slotHours * 60 + slotMinutes);
                     }
                     
                     
                     const isAlignedSession = slotData && 
                       formatTime(slotData.originalTime) !== time &&
                       !slotData.isAvailability;
                    return (
                      <div 
                        key={`${dayKey}-${time}`} 
                        className={`time-slot ${time==="1:00 PM"?'bigger':''} ${isToday ? 'today-slot' : ''} ${isUpcomingDay ? 'upcoming-slot' : ''}`}
                      >
                        {isUpcomingDay ? (
                          <div className="span-session">
                            <div className="span-session-time">Not Available yet</div>
                          </div>
                        ):
                        isUnavailable ? (
                          <div className="span-session">
                            <div className="span-session-time">CLOSED</div>
                          </div>
                        )
                        
                        
                        : organizedData[dayKey]?.[time]?.length > 0 ? (
                          organizedData[dayKey][time].map((slotData, idx) => {
                            if (slotData.isAvailability) {
                              // Availability slot
                              return (
                                <div 
                                  key={`${dayKey}-${time}-${idx}`}
                                  className={`session availability ${isChecked ? 'da-checked' : ''} ${isUnavailable ? 'unavailable' : ''}`}
                                  onClick={() => !isUnavailable && handleCheckboxChange(day, time)}
                                  style={{ top: `${slotData.diffMinutes}px` }}

                                >
                                  {isChecked ? (
                                    <div>
                                      <div className="student-name">Demo Available</div>
                                      <div className="session-time">{time}</div>
                                    </div>
                                  ) : (
                                    <div>
                                      <div className="session-time">{time}</div>
                                    </div>
                                  )}
                                </div>
                              );
                            } else {
                              // Scheduled session
                              const isAlignedSession = formatTime(slotData.originalTime) !== time;
                              const topOffset = isAlignedSession 
                              ? calculateTopOffset(slotData.originalTime, time)
                              : 0;
                              return (
                                <div
                                  key={`${dayKey}-${time}-${idx}`}
                                  className={`session ${slotData.type} ${isAlignedSession ? 'aligned-session' : ''}`}
                                  style={{
                                    top: `${topOffset}px`,
                                  }}
                                >
                                  <div className="student-name">
                                    {slotData.FullName}
                                  </div>
                                  <div className="session-time">    
                                    {formatTime(slotData.originalTime || slotData.ClassDateTime)}
                                  </div>
                                  {slotData.type === 'demo' ? (
                                    <div className="demo-text">Demo</div>
                                  ) : (
                                    <div className="session-count">
                                      {slotData.sessionsTaken}/{slotData.totalSessions}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                          })
                        ) : (
                          // Empty slot
                          <div 
                            className={`${isUnavailable ? 'span-session' : ''} ${isChecked ? 'session empty da-checked' : 'session empty'}`}
                            onClick={() => !isUnavailable && handleCheckboxChange(day, time)}
                          >
                            {isChecked ? (
                              <div>
                                <div className="student-name">Demo Available</div>
                                <div className="session-time">{time}</div>
                              </div>
                            ) : isUnavailable ? (
                              <div className="span-session-time">CLOSED</div>
                            ) : (
                              <div className="session-time"
                              style={{lineHeight:"60px"}}
                              >{time}</div>
                            )}
                          </div>
                        )}
                        </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const formatDayHeader = (date) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const dayName = days[date.getDay()];
  const dayNum = date.getDate();
  const monthName = months[date.getMonth()];
  
  return `${dayName} ${dayNum}-${monthName}`;
};

export default DemoAvailability;